import type { VectorKbKnowledgeSource } from "./vectorKbCapability";

/** Registry binding that returns one document's extracted markdown (LADR-030). */
export const MARKDOWN_RETRIEVE_BINDING = "markdown.retrieve";

/** Matches the API model's `max_length=8` on `org_sources`. */
export const MAX_MARKDOWN_ORG_SOURCES = 8;

export const DEFAULT_MARKDOWN_MAX_CHARS = 20000;

export type MarkdownOrgSource = {
  id: string;
  category: VectorKbKnowledgeSource["category"];
};

export type MarkdownRetrieveConfig = {
  org_sources: MarkdownOrgSource[];
  max_chars: number;
};

/**
 * The tool returns one of two shapes, discriminated by `available`. A document
 * that was catalogued but never had markdown extracted comes back as
 * `available: false` with a `reason` instead of `content`, so the agent reports
 * it rather than retrying a permanently missing file.
 */
export const MARKDOWN_RETRIEVE_OUTPUT_SCHEMA = {
  type: "object",
  properties: {
    doc_id: { type: "string" },
    doc_name: { type: "string" },
    available: { type: "boolean" },
    content: { type: "string" },
    truncated: { type: "boolean" },
    reason: { type: "string" },
  },
  required: ["doc_id", "doc_name", "available"],
};

export function defaultMarkdownRetrieveConfig(): MarkdownRetrieveConfig {
  return {
    org_sources: [{ id: "", category: "internal" }],
    max_chars: DEFAULT_MARKDOWN_MAX_CHARS,
  };
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function isOrgCategory(
  value: unknown,
): value is MarkdownOrgSource["category"] {
  return value === "public" || value === "internal" || value === "personal";
}

function parseOrgSources(value: unknown): MarkdownOrgSource[] {
  if (!Array.isArray(value)) {
    return [];
  }
  return value.flatMap((item) => {
    if (!isRecord(item) || typeof item.id !== "string") {
      return [];
    }
    if (!isOrgCategory(item.category)) {
      return [];
    }
    return [{ id: item.id, category: item.category }];
  });
}

/** Interpret opaque catalog/grant ``config`` as ``markdown.retrieve`` settings. */
export function parseMarkdownRetrieveConfig(
  config: unknown,
): MarkdownRetrieveConfig {
  if (!isRecord(config)) {
    return { org_sources: [], max_chars: DEFAULT_MARKDOWN_MAX_CHARS };
  }
  const maxChars = config.max_chars;
  return {
    org_sources: parseOrgSources(config.org_sources),
    max_chars:
      typeof maxChars === "number" && Number.isFinite(maxChars)
        ? maxChars
        : DEFAULT_MARKDOWN_MAX_CHARS,
  };
}

function hasDuplicateOrgSource(sources: MarkdownOrgSource[]): boolean {
  const keys = sources.map((source) => `${source.category}::${source.id}`);
  return new Set(keys).size !== keys.length;
}

/** Mirrors the API's ``MarkdownRetrieveConfig`` validators. */
export function getMarkdownRetrieveConfigValidationError(
  config: MarkdownRetrieveConfig,
): string | null {
  if (config.org_sources.some((source) => !source.id)) {
    return "Select an organisation for every source.";
  }
  if (config.org_sources.length === 0) {
    return "Add at least one document source.";
  }
  if (config.org_sources.length > MAX_MARKDOWN_ORG_SOURCES) {
    return `Up to ${MAX_MARKDOWN_ORG_SOURCES} document sources are allowed.`;
  }
  if (hasDuplicateOrgSource(config.org_sources)) {
    return "Each document source must be a different organisation.";
  }
  if (!Number.isInteger(config.max_chars) || config.max_chars < 1) {
    return "Max characters must be an integer of at least 1.";
  }
  return null;
}

export function serializeMarkdownRetrieveConfig(
  config: MarkdownRetrieveConfig,
): Record<string, unknown> {
  return { org_sources: config.org_sources, max_chars: config.max_chars };
}

export function formatMarkdownRetrieveConfigSummary(
  config: MarkdownRetrieveConfig,
): string {
  return `${config.max_chars.toLocaleString("en-NZ")} characters max per document`;
}
